import { useBrandingContextNoThrow } from "@@/routes/shared/branding-context";
import { Global, useTheme } from "@emotion/react";
import type { BrandingBackground } from "@towni/common";
import {
    colorAsString,
    gradientToCssValue,
    isStorageItemImageReference,
} from "@towni/common";

import { defaultReceiptBrandingBackgroundCss } from "@@/routes/shared/default-branding";
import { browserLogger } from "@@/settings";
import { useGetImageUrl } from "@@/shared/image-url";
import { lightColorTheme } from "@@/styles/themes/default.theme";

const useBackground = (background: BrandingBackground | undefined) => {
    const getImageUrl = useGetImageUrl();

    // Always render the background div, but handle empty case
    if (!background) return { backgroundStyles: {}, shouldRender: false };

    const backgroundColor = background.color
        ? colorAsString(background.color)
        : lightColorTheme.white.main.light95.asString;

    // Process image if it exists
    const backgroundImageUrl =
        typeof background.image === "string"
            ? background.image
            : isStorageItemImageReference(background?.image)
              ? getImageUrl(background?.image, { imageWidth: "full" })
              : undefined;

    const backgroundImage = backgroundImageUrl?.trim().length
        ? backgroundImageUrl
        : undefined;

    const backgroundImageCssValue = backgroundImage
        ? `url(${backgroundImage})`
        : undefined;

    const backgroundGradientCssValue = background.gradient
        ? gradientToCssValue(background.gradient)
        : undefined;

    // Create base background styles
    let baseBackgroundStyles = {
        backgroundColor: backgroundColor,
    } as {
        backgroundColor: string;
        backgroundSize?: string;
        backgroundRepeat?: string;
        backgroundPosition?: string;
        backgroundImage?: string;
    };

    // Add image-specific properties if we have an image
    if (backgroundImageCssValue) {
        baseBackgroundStyles = {
            ...baseBackgroundStyles,
            backgroundImage: backgroundImageCssValue,
            backgroundSize: background.cover === "COVER" ? "cover" : "auto",
            backgroundRepeat:
                background.cover === "COVER" ? "no-repeat" : "repeat",
            backgroundPosition: "top",
        };
    }
    // Add gradient-specific properties if we have a gradient and no image
    else if (backgroundGradientCssValue) {
        baseBackgroundStyles = {
            ...baseBackgroundStyles,
            backgroundImage: backgroundGradientCssValue,
            backgroundSize: background.cover === "COVER" ? "cover" : "auto",
            backgroundRepeat:
                background.cover === "COVER" ? "no-repeat" : "repeat",
            backgroundPosition: "top",
        };
    }

    browserLogger.info({
        baseBackgroundStyles,
    });

    // Performance optimizations for images/gradients
    const performanceOptimizations =
        backgroundImageCssValue || backgroundGradientCssValue
            ? {
                  transform: "translate3d(0,0,0)",
                  willChange: "transform",
                  WebkitBackfaceVisibility: "hidden" as const,
                  WebkitPerspective: 1000,
              }
            : {};

    return {
        backgroundStyles: {
            ...baseBackgroundStyles,
            ...performanceOptimizations,
        },
        shouldRender: true,
    };
};

type GlobalBackgroundDivProps = {
    fallBackToReceipt?: boolean;
};

const GlobalBackgroundDiv = (props: GlobalBackgroundDivProps) => {
    const theme = useTheme();
    const context = useBrandingContextNoThrow();

    const branding = context?.branding;

    const brandingB = () => {
        // Check we have a brand from provider
        if (branding?.providerId) {
            return (
                branding.background ?? branding.product?.peripheralBackground
            );
        } else {
            //Used by quote accepte page
            if (props.fallBackToReceipt) {
                return defaultReceiptBrandingBackgroundCss;
            }
        }
        //Default branding
        return branding?.background;
    };

    const { backgroundStyles, shouldRender } = useBackground(brandingB());

    if (!shouldRender) return null;

    return (
        <Global
            styles={`
                body {
                    ${Object.entries(backgroundStyles)
                        .map(
                            ([key, value]) =>
                                `${key.replace(/([A-Z])/g, "-$1").toLowerCase()}: ${value};`,
                        )
                        .join("\n                    ")}
                }

                #root {
                    background-color: ${theme.colors.transparent.asString};
                }
            `}
        />
    );
};

export { GlobalBackgroundDiv };
